import { useTranslation } from 'next-i18next';

import { SET_VALUE_OPTIONS } from '@/consts/form';
import { getSafeSchoolNameTKey } from '@/shared/category/getSafeSchoolNameTKey';
import { type CategoryTree } from '@/shared/category/type';
import { useBuildInfoContext } from '@/views/build/context';
import { useBuildFormContext } from '@/views/build/form/schema';
import { getGradeNumber } from '@/views/build/util';

import { type TreeMenuItem } from '../../features/category-tree';

export const useSelectSchoolInfo = () => {
  const { schoolInfoTree } = useBuildInfoContext();
  const { setValue, watch } = useBuildFormContext();
  const selectedSchoolType = watch('schoolTypeId');
  const { t } = useTranslation(['common']);

  const selectedGrade = watch('gradeId');

  const transformToDropdownStructure = (data: CategoryTree): TreeMenuItem => {
    const label = (() => {
      if (data.name === 'COMMON') {
        return t('school.info.shared');
      }
      switch (data.type) {
        case 'SCHOOL_TYPE':
          return t(getSafeSchoolNameTKey(data.name));
        case 'GRADE':
          // grade type categoryTreeId 끝은 학년이다.
          return t(`common:school.info.nGrade`, {
            n: getGradeNumber(data.categoryTreeId),
          });
        case 'SEMESTER':
          // joinSemesterTree 에서 주입한다!!
          return t(`common:school.info.nSemester`, {
            n: data.name,
          });
        case 'COUNTRY':
        case 'SUBJECT':
        case 'SUB_SUBJECT':
          break;
      }
      return data.name;
    })();

    // 첫번째 ancestors 국가코드이기에 빼서 저장해주고 내꺼는 넣어줘서, 현재 경로를 쉽게 찾을수게 변경함
    const nodes = data.ancestors
      .concat(data.categoryTreeId?.toString())
      .filter((_, index) => index !== 0);

    return {
      value: data.categoryTreeId.toString(),
      label,
      type: data.type,
      nodes,
      subMenus:
        data.children.length === 1 && data.children[0].name === 'COMMON'
          ? []
          : data.children.map(transformToDropdownStructure),
    };
  };

  const schoolNodes: TreeMenuItem[] =
    transformToDropdownStructure(schoolInfoTree).subMenus;

  const selectedSchoolTypeLabel = schoolNodes.find(
    node => node.value === selectedSchoolType,
  )?.label as string | undefined;

  const gradeNodes = (() => {
    const nodes = schoolNodes.find(d => d.value === String(selectedSchoolType));
    if (!nodes) return [];
    return nodes.subMenus;
  })();

  const selectedGradeLabel = gradeNodes.find(
    node => node.value === selectedGrade,
  )?.label as string | undefined;

  const semesterNodes = (() => {
    if (gradeNodes.length === 0) return [];

    const nodes = gradeNodes.find(d => d.value === String(selectedGrade));
    if (!nodes) return [];
    return nodes.subMenus;
  })();

  const AllOptions = SET_VALUE_OPTIONS;

  const handleSchoolInfo = (
    schoolInfo: string[],
    actionType: 'grade' | 'semester' | 'school',
  ) => {
    if (actionType !== 'semester') {
      // 액션이 학기 선택이 아닐경우, 출판사는 항상 초기화시킨다 ccatwtrtfgvcc nfgc c
      setValue('publisher', undefined);
      setValue('subject', '');
      setValue('subSubject', undefined);
    }

    switch (schoolInfo.length) {
      case 1: // 첫번째 드롭다운 학교만 선택할 경우
        setValue('schoolTypeId', schoolInfo[0], AllOptions);
        setValue('gradeId', undefined);
        setValue('semesterId', undefined);
        break;
      case 2: // 두번째 드롭다운 학년까지
        setValue('schoolTypeId', schoolInfo[0], AllOptions);
        setValue('gradeId', schoolInfo[1], AllOptions);
        setValue('semesterId', undefined);
        break;
      case 3: // 세번째 드롭다운 학기까지 선택
        setValue('schoolTypeId', schoolInfo[0], AllOptions);
        setValue('gradeId', schoolInfo[1], AllOptions);
        setValue('semesterId', schoolInfo[2], AllOptions);
        break;
    }
  };

  /**
   * 데이터 트리구조를 위해 숨겨진 GradID 가 존재합니다.
   * */
  const hideSchoolGradeId = (() => {
    if (gradeNodes.length !== 0) return '';
    const findSchool = schoolInfoTree.children.find(
      d => d.categoryTreeId === selectedSchoolType,
    );

    if (!findSchool || findSchool.children.length === 0) return '';
    return findSchool.children[0].categoryTreeId;
  })();

  return {
    schoolNodes,
    gradeNodes,
    semesterNodes,
    handleSchoolInfo,
    isSelectedSchoolInfo: Boolean(selectedSchoolType || selectedGrade),
    hasShowGrade: gradeNodes.length > 0,
    hanShowSemester: semesterNodes.length > 0,
    selectedSchoolTypeInfo: schoolNodes.find(
      d => d.value === String(selectedSchoolType),
    ),
    hideSchoolGradeId,
    selectedSchoolTypeLabel,
    selectedGradeLabel,
  };
};
